import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { db } from './firebase';
import { ClickableStudioName } from './utils/ClickableNames';

export default function StudioSearch() {
  const [allStudios, setAllStudios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    loadStudios();
  }, []);

  async function loadStudios() {
    try {
      setLoading(true);
      setError(null);
      
      console.log("🏛️ Starting to load studios...");
      
      // Load from old structure first
      const compsRef = collection(db, "competitions");
      const snapshot = await getDocs(compsRef);
      
      console.log("🏛️ Found", snapshot.size, "competitions");
      
      const studioSet = new Set();
      
      snapshot.forEach((doc) => {
        const comp = doc.data();
        if (comp.results && Array.isArray(comp.results)) {
          comp.results.forEach((result) => {
            if (result.studio && result.studio.trim() !== '') {
              studioSet.add(result.studio.trim());
            }
          });
        }
      });
      
      // Load from new structure
      try {
        const disciplinesSnapshot = await getDocs(collection(db, "disciplines"));
        
        for (const disciplineDoc of disciplinesSnapshot.docs) {
          const disciplineName = disciplineDoc.id;
          const categoriesSnapshot = await getDocs(collection(db, "disciplines", disciplineName, "categories"));
          
          for (const categoryDoc of categoriesSnapshot.docs) {
            const categoryName = categoryDoc.id;
            
            // Check category-level competitions
            try {
              const categoryCompsSnapshot = await getDocs(collection(db, "disciplines", disciplineName, "categories", categoryName, "competitions"));
              
              categoryCompsSnapshot.forEach((doc) => {
                const comp = doc.data();
                if (comp.results && Array.isArray(comp.results)) {
                  comp.results.forEach((result) => {
                    if (result.studio && result.studio.trim() !== '') {
                      studioSet.add(result.studio.trim());
                    }
                  });
                }
              });
            } catch (error) {
              // Collection might not exist
            }
            
            // Check dance-specific competitions
            try {
              const dancesSnapshot = await getDocs(collection(db, "disciplines", disciplineName, "categories", categoryName, "dances"));
              
              for (const danceDoc of dancesSnapshot.docs) {
                const danceName = danceDoc.id;
                const danceCompsSnapshot = await getDocs(collection(db, "disciplines", disciplineName, "categories", categoryName, "dances", danceName, "competitions"));
                
                danceCompsSnapshot.forEach((doc) => {
                  const comp = doc.data();
                  if (comp.results && Array.isArray(comp.results)) {
                    comp.results.forEach((result) => {
                      if (result.studio && result.studio.trim() !== '') {
                        studioSet.add(result.studio.trim());
                      }
                    });
                  }
                });
              }
            } catch (error) {
              // Collection might not exist
            }
          }
        }
      } catch (error) {
        console.warn("Could not load from new structure:", error);
      }
      
      const studioList = Array.from(studioSet).sort();
      console.log("🏛️ Found", studioList.length, "unique studios");
      console.log("🏛️ First 10 studios:", studioList.slice(0, 10));
      
      setAllStudios(studioList);
      
    } catch (err) {
      console.error("❌ Error loading studios:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  const filteredStudios = searchTerm.length >= 2 
    ? allStudios.filter(name => 
        name.toLowerCase().includes(searchTerm.toLowerCase())
      ).slice(0, 10)
    : [];

  if (loading) {
    return (
      <div style={{ padding: "2rem", textAlign: "center", maxWidth: "1200px", margin: "0 auto" }}>
        <h2>🏛️ Studio Search</h2>
        <p>Loading studios...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: "2rem", textAlign: "center", maxWidth: "1200px", margin: "0 auto" }}>
        <h2>🏛️ Studio Search</h2>
        <p style={{ color: 'red' }}>Error: {error}</p>
      </div>
    );
  }

  return (
    <div style={{ padding: "2rem", maxWidth: "1200px", margin: "0 auto" }}>
      <h2>🏛️ Studio Search</h2>
      <p style={{ color: "#666", marginBottom: "2rem" }}>
        Search through {allStudios.length} dance studios in our database. Type at least 2 characters to see suggestions.
      </p>
      
      <div style={{ 
        marginBottom: "2rem", 
        padding: "1rem", 
        backgroundColor: "#f5f5f5", 
        borderRadius: "8px"
      }}>
        <input
          type="text"
          placeholder="Search for a studio by name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            width: '100%',
            padding: '1rem',
            fontSize: '1.1rem',
            border: '2px solid #ddd',
            borderRadius: '8px',
            outline: 'none',
            transition: 'border-color 0.3s',
            boxSizing: 'border-box'
          }}
          onFocus={(e) => e.target.style.borderColor = '#0066cc'}
          onBlur={(e) => e.target.style.borderColor = '#ddd'}
        />
      </div>
      
      {searchTerm.length >= 2 && (
        <div>
          <div style={{ marginBottom: "1rem", color: "#666" }}>
            <p>📊 Found {filteredStudios.length} studios matching "{searchTerm}"</p>
          </div>
          
          {filteredStudios.length > 0 ? (
            <div style={{ 
              backgroundColor: "white",
              borderRadius: "8px",
              overflow: "hidden",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
            }}>
              {filteredStudios.map((name, index) => (
                <div key={index} style={{ 
                  borderBottom: index < filteredStudios.length - 1 ? "1px solid #eee" : "none"
                }}>
                  <ClickableStudioName
                    name={name}
                    style={{ 
                      display: 'block',
                      padding: '1.5rem',
                      color: '#333', 
                      textDecoration: 'none',
                      fontSize: '1.2rem',
                      fontWeight: 'bold',
                      transition: 'all 0.3s ease',
                      backgroundColor: 'transparent'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#f8f9fa';
                      e.target.style.color = '#0066cc';
                      e.target.style.paddingLeft = '2rem';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.color = '#333';
                      e.target.style.paddingLeft = '1.5rem';
                    }}
                  >
                    🏛️ {name}
                  </ClickableStudioName>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ 
              textAlign: "center", 
              padding: "3rem", 
              color: "#666",
              backgroundColor: "#f9f9f9",
              borderRadius: "8px"
            }}>
              <p>No studios found matching "{searchTerm}"</p>
              <p style={{ fontSize: "0.9rem" }}>Try a different spelling or fewer characters</p>
            </div>
          )}
        </div>
      )}
      
      {searchTerm.length < 2 && searchTerm.length > 0 && (
        <div style={{ 
          textAlign: "center", 
          padding: "2rem", 
          color: "#666",
          backgroundColor: "#f9f9f9",
          borderRadius: "8px"
        }}>
          <p>Type at least 2 characters to start searching</p>
        </div>
      )}
    </div>
  );
}
